// @ts-nocheck
class AgeGate {
  static #COOKIE_NAME = 'age_verified';
  static #COOKIE_VALUE = 'verified';

  // Returns the actual tab stops inside the overlay, in DOM order.
  // Shopify localization forms add hidden inputs, which must not be included:
  // calling focus() on one leaves focus on the final CTA instead of cycling.
  static #getFocusable(overlay) {
    return Array.from(
      overlay.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => {
      const style = window.getComputedStyle(element);
      return (
        !element.hasAttribute('hidden') &&
        element.getAttribute('aria-hidden') !== 'true' &&
        element.tabIndex >= 0 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden'
      );
    });
  }

  static verify() {
    document.cookie = `${this.#COOKIE_NAME}=${this.#COOKIE_VALUE}; path=/; SameSite=Lax; Max-Age=2592000`;
    document.documentElement.classList.remove('agewall-active', 'age-gate-restricted-experience');
    document.documentElement.classList.add('age-verified');
    if (window.themeSettings) window.themeSettings.ageGateRestrictedExperience = false;

    // F-1: Restore inert siblings so background content is interactive again.
    // Only touch elements that could have had inert applied (mirrors init exclusions).
    Array.from(document.body.children).forEach((el) => {
      if (!el.hasAttribute('popover') && el.id !== 'PBarNextFrameWrapper') {
        el.removeAttribute('inert');
      }
    });

    // F-3: Return focus to #MainContent so keyboard/AT users land in a
    //       sensible place after the gate closes.
    const mainContent = document.getElementById('MainContent');
    if (mainContent) {
      if (!mainContent.hasAttribute('tabindex')) mainContent.setAttribute('tabindex', '-1');
      mainContent.focus({ preventScroll: true });
      mainContent.removeAttribute('tabindex');
    }

    // Mobile Safari can preserve the document offset that existed behind the fixed
    // age gate. Reset both possible scrolling roots after the overlay styles clear.
    if (window.matchMedia('(max-width: 749px)').matches) {
      const scrollToTop = () => {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        window.scrollTo({ left: 0, top: 0, behavior: 'auto' });
      };
      scrollToTop();
      requestAnimationFrame(scrollToTop);
    }
  }

  static #publishVerification(verified) {
    try {
      const analytics = window.OCSAnalyticsEvents;
      if (!analytics || typeof analytics.publishOcsAnalyticsEvent !== 'function') return;

      analytics.publishOcsAnalyticsEvent('ocs:age_verification', {
        age_verified: verified === true,
        locale: document.documentElement.lang || window.Shopify?.locale || '',
      });
    } catch (error) {
      /* Analytics must never interrupt successful age verification. */
    }
  }

  static init() {
    const overlay = document.getElementById('age-gate');
    if (!overlay) return;
    if (!document.documentElement.classList.contains('agewall-active')) return;

    // F-1: Make every body sibling inert so AT and keyboard cannot reach
    //       background content while the gate is open.
    // Exclude: the overlay itself, any popover element (e.g. Shopify preview
    //           bar uses popover="manual"), and #PBarNextFrameWrapper.
    Array.from(document.body.children).forEach((el) => {
      if (
        el !== overlay &&
        !el.hasAttribute('popover') &&
        el.id !== 'PBarNextFrameWrapper'
      ) {
        el.setAttribute('inert', '');
      }
    });

    this.#setupLangSelect(overlay);
    this.#setupForm(overlay);
    this.#setupFocusTrap(overlay);

    // Start at the first control in DOM order. This makes the forward tab
    // sequence predictable and lets the final CTA loop back to this control.
    const firstFocusable = AgeGate.#getFocusable(overlay)[0];
    if (firstFocusable) firstFocusable.focus();
  }

  /**
   * Keep keyboard focus in the mandatory age-gate dialog. The focusable list
   * is read at each Tab press so Verify enters the sequence when it becomes
   * enabled after a complete date of birth is entered.
   * @param {HTMLElement} overlay
   */
  static #setupFocusTrap(overlay) {
    overlay.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        const firstDobInput = document.getElementById('age-gate-month');
        const firstFocusable = AgeGate.#getFocusable(overlay)[0];
        (firstDobInput || firstFocusable)?.focus();

        const statusEl = document.getElementById('age-gate-status');
        if (statusEl) {
          const message = overlay.dataset.escapeMsg || '';
          statusEl.textContent = '';
          requestAnimationFrame(() => { statusEl.textContent = message; });
        }
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = AgeGate.#getFocusable(overlay);
      if (focusable.length < 2) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  // L-1: Wire the language select in JS rather than via an inline onchange
  //       attribute. Submitting on 'change' fires while keyboard users arrow
  //       through options, changing the locale before they finish choosing.
  //       Listening on 'change' is fine for pointer users; the issue only
  //       manifests when the select is navigated with arrow keys and the
  //       browser fires 'change' on each keypress (Windows + some browsers).
  //       The guard below checks that the new value differs from the
  //       page's current locale before submitting, which prevents spurious
  //       reloads but does not fully fix arrow-key churn.
  //
  //       The most reliable keyboard-safe pattern is to submit only on an
  //       explicit commit (Enter key or blur after arrow-key navigation).
  //       We use a 'keydown' guard: if the last interaction was an arrow key
  //       we defer submission until the user presses Enter or the select
  //       loses focus.
  static #setupLangSelect(overlay) {
    const select = document.getElementById('age-gate-lang-select');
    const form   = document.getElementById('age-gate-lang-form');
    if (!select || !form) return;

    let navigatingWithKeys = false;

    select.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        navigatingWithKeys = true;
      }
      if (e.key === 'Enter' && navigatingWithKeys) {
        navigatingWithKeys = false;
        form.submit();
      }
    });

    select.addEventListener('blur', () => {
      // Commit on blur if the user had been navigating with keys and left
      // the select without pressing Enter (e.g. Tab away).
      if (navigatingWithKeys) {
        navigatingWithKeys = false;
        form.submit();
      }
    });

    select.addEventListener('change', () => {
      // For pointer/touch users 'change' fires once on confirmed selection —
      // submit immediately. For keyboard users navigatingWithKeys is true and
      // we wait for Enter/blur instead.
      if (!navigatingWithKeys) {
        form.submit();
      }
    });
  }

  static #setupForm(overlay) {
    const monthInput = document.getElementById('age-gate-month');
    const dayInput   = document.getElementById('age-gate-day');
    const yearInput  = document.getElementById('age-gate-year');
    const verifyBtn  = document.getElementById('age-gate-verify');
    const form       = document.getElementById('age-gate-form');
    const dobInputs  = [monthInput, dayInput, yearInput];

    if (!monthInput || !dayInput || !yearInput || !verifyBtn || !form) return;

    // I-1: Clear aria-invalid / aria-describedby from all DOB fields.
    const clearInputAriaStates = () => {
      dobInputs.forEach((input) => {
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
      });
    };

    const hideErrors = () => {
      overlay.querySelectorAll('.age-gate__msg').forEach((m) => { m.hidden = true; });
      clearInputAriaStates();
    };

    // The authored copy of each message, read once, while it is guaranteed to
    // still be there. showError() blanks a message before re-inserting its text
    // (see below) and must never read that text back out of the DOM: two calls in
    // the same frame would have the second one read the first one's blank, and
    // restore it. The copy would be gone for the rest of the page session, and
    // because base.css hides `p:empty` the loss showed as no message at all
    // rather than as an empty red line. Every later error was silent too, since
    // each one re-read the paragraph it had already emptied.
    const messageText = new Map();
    overlay.querySelectorAll('.age-gate__msg').forEach((m) => {
      messageText.set(m, m.textContent.trim());
    });

    // I-1: Mark all DOB fields as invalid and point them at the live error.
    // Announcement reliability: we clear the element's text before re-setting it
    // so AT treats it as a new content insertion even if the same error fires twice.
    // (Some SRs only fire role="alert" on content change, not on hidden→visible toggle.)
    const showError = (type) => {
      hideErrors();
      const msg = overlay.querySelector(`.age-gate__msg--${type}`);
      if (msg) {
        const savedText = messageText.get(msg) ?? msg.textContent.trim();
        msg.textContent = '';
        msg.hidden = false;
        // Re-insert text in the next frame so the DOM mutation registers as new content.
        requestAnimationFrame(() => {
          msg.textContent = savedText;
        });
        dobInputs.forEach((input) => {
          input.setAttribute('aria-invalid', 'true');
          input.setAttribute('aria-describedby', msg.id);
        });
      }
    };

    const checkComplete = () => {
      // Enable as soon as every field has an entry. Full date validation stays
      // in the submit handler so incomplete or invalid dates receive its error.
      const complete =
        monthInput.value.length > 0 &&
        dayInput.value.length > 0 &&
        yearInput.value.length > 0;
      // Use only the native disabled attribute — aria-disabled is redundant
      // when disabled is present and causes double-announcement in some SRs.
      verifyBtn.disabled = !complete;
      verifyBtn.classList.toggle('age-gate__verify-btn--ready', complete);
    };

    const enforceNumeric = (input) => {
      const cleaned = input.value.replace(/\D/g, '');
      if (cleaned !== input.value) input.value = cleaned;
    };

    const autoAdvance = (current, next) => {
      if (next && current.value.length >= current.maxLength) next.focus();
    };

    // Auto-retreat: the moment a field is cleared by a deletion (backspace/delete),
    // hop focus back to the previous field (year → day → month) with the caret at
    // the end so the user can keep deleting. The inputType guard means clearing via
    // deletion moves focus, but a stripped non-digit keystroke does not.
    const autoRetreat = (current, prev) => {
      if (!prev) return;
      current.addEventListener('input', (e) => {
        const isDeletion = !e.inputType || e.inputType.startsWith('delete');
        if (current.value === '' && isDeletion) {
          prev.focus();
          const end = prev.value.length;
          prev.setSelectionRange(end, end);
        }
      });
    };

    autoRetreat(dayInput, monthInput);
    autoRetreat(yearInput, dayInput);

    monthInput.addEventListener('input', () => {
      enforceNumeric(monthInput);
      hideErrors();
      checkComplete();
      autoAdvance(monthInput, dayInput);
    });

    dayInput.addEventListener('input', () => {
      enforceNumeric(dayInput);
      hideErrors();
      checkComplete();
      autoAdvance(dayInput, yearInput);
    });

    yearInput.addEventListener('input', () => {
      enforceNumeric(yearInput);
      hideErrors();
      checkComplete();
    });

    form.addEventListener('submit', (e) => {
      e.preventDefault();

      const month = parseInt(monthInput.value, 10);
      const day   = parseInt(dayInput.value, 10);
      const year  = parseInt(yearInput.value, 10);
      const currentYear = new Date().getFullYear();
      const minYear = parseInt(yearInput.dataset.min, 10) || currentYear - 120;
      const maxYear = parseInt(yearInput.dataset.max, 10) || currentYear;

      if (
        !month || month < 1 || month > 12 ||
        !day   || day   < 1 || day   > 31 ||
        !year  || year  < minYear || year > maxYear
      ) {
        showError('invalid');
        AgeGate.#publishVerification(false);
        return;
      }

      const dob = new Date(year, month - 1, day);
      if (dob.getMonth() !== month - 1 || dob.getDate() !== day) {
        showError('invalid');
        AgeGate.#publishVerification(false);
        return;
      }

      const today = new Date();
      let age = today.getFullYear() - year;
      const monthDiff = today.getMonth() - (month - 1);
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) age--;

      if (age < 19) {
        showError('underage');
        AgeGate.#publishVerification(false);
        return;
      }

      AgeGate.verify();
      AgeGate.#publishVerification(true);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  AgeGate.init();
});
